import { createFileRoute } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/Layout";
import { Section } from "@/components/site/Section";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy - Kristan Cole Network" },
      {
        name: "description",
        content:
          "How the Kristan Cole Network collects, uses, shares, and protects personal data on this site and through associated third-party client websites.",
      },
      { property: "og:title", content: "Privacy Policy - Kristan Cole Network" },
      {
        property: "og:description",
        content:
          "How KCN collects, uses, shares, and protects your personal data.",
      },
    ],
    links: [{ rel: "canonical", href: "https://joinkcn.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="container-edge">
          <div className="max-w-3xl mx-auto pt-32 md:pt-40 pb-8">
            <p className="eyebrow text-foreground/70 mb-6">Legal</p>
            <h1 className="font-display font-light tracking-[-0.02em] leading-[1.05] text-[clamp(2.25rem,4vw,3.5rem)]">
              Privacy Policy
            </h1>
            <p className="mt-6 text-foreground/70 text-sm">
              Last modified: October 6, 2026
            </p>
          </div>
        </div>
      </section>

      <Section tone="dark" className="!pt-4 !pb-12 md:!pb-16">
        <div className="max-w-3xl mx-auto prose-legal [&>h2:first-child]:mt-0">
          <h2>Who we are</h2>
          <p>Our website address is: https://joinkcn.com/</p>

          <h2>SMS Messaging Privacy</h2>
          <p>
            Kristan Cole Network, a DBA of Cole Investments Inc., may collect
            mobile phone numbers and SMS messaging consent when individuals
            choose to receive text messages from us. We use this information to
            provide appointment confirmations, scheduling information,
            reminders, responses to inquiries, and related communications
            requested by the recipient.
          </p>
          <p>
            Message frequency varies. Message and data rates may apply. You may
            opt out of SMS communications at any time by replying STOP. Reply
            HELP for assistance.
          </p>
          <p>
            We do not sell or share your SMS opt-in data or personal information
            with third parties for marketing purposes. SMS consent and mobile
            information will not be shared with third parties or affiliates for
            marketing or promotional purposes.
          </p>

          <h2>Embedded content from other websites</h2>
          <p>
            Articles on this site may include embedded content (e.g. videos,
            images, articles, etc.). Embedded content from other websites
            behaves in the exact same way as if the visitor has visited the
            other website.
          </p>
          <p>
            These websites may collect data about you, use cookies, embed
            additional third-party tracking, and monitor your interaction with
            that embedded content, including tracking your interaction with the
            embedded content if you have an account and are logged in to that
            website.
          </p>

          <h2>Our Privacy Policy</h2>
          <p>
            Your privacy is very important to all of us at Kristan Cole
            Network. This Privacy Policy explains the ways in which we collect,
            use, share and protect personal data ("Personal Data") on this
            website ("Site") and through any Third Party Client websites
            associated with this Site.
          </p>
          <p>
            This Privacy Policy applies to persons and businesses anywhere in
            the world that may access the Site. By using the Site you agree to
            be bound by this Privacy Policy in its most recent iteration. If
            you do not wish to provide Personal Data or be bound by this
            Privacy Policy, you may not use the Site or the Services.
          </p>
          <p>
            We reserve the right to modify or replace this Privacy Policy at
            any time by updating this document. You agree to be bound by the
            most recent version of the Privacy Policy available on the Site.
          </p>

          <h3>Personal Data Collection</h3>
          <p>
            We collect Personal Data from people when they access our Site or
            our Services. Our legal basis for collecting and processing your
            Personal Data is (i) your explicit consent when accessing the Site
            or Services and (ii) a legitimate interest and need in order to
            provide the Services. We may collect the following Personal Data
            from users:
          </p>
          <ul>
            <li>
              <strong>Information you submit to us or our Third-Party
              Clients.</strong> If you submit information through the Site,
              including through any contact form, live help feature, or other
              submission functionality, we will collect any Personal Data you
              submit.
            </li>
            <li>
              <strong>Information you submit when receiving the Services.</strong>{" "}
              We will collect any Personal Data you share in order to become a
              client or when you work with us to receive the Services. In
              addition, if you become a client of, or receive any Services
              from, any of our Third-Party Clients, we will collect any
              Personal Data you transmit to receive Services from Third-Party
              Clients. This Personal Data may include, but is not limited to,
              your name, company information, customer information, email
              address, phone number, mailing address, bank account
              information, debit card information, and other verification or
              fraud prevention related information.
            </li>
            <li>
              <strong>Information automatically collected by the Site.</strong>{" "}
              We may use cookies, log files, clear .gifs, and other analytics
              tools to track the ways in which you use and interact with the
              Site, the Services, and other uses. This information helps us
              analyze and improve our features. You may be able to disable
              cookies in your web browser or mobile device. However, please
              note that disabling cookies may prevent you from taking full
              advantage of the Site or its Services. For more information
              about the use of cookies and how to block or disable them, you
              may visit allaboutcookies.org or youronlinechoices.eu.
            </li>
            <li>
              <strong>Information required for technical or customer
              support.</strong> If you contact us for technical or customer
              support, we may collect any Personal Data you provide to us.
            </li>
          </ul>

          <h3>Personal Data Use and Sharing</h3>
          <p>
            We use Personal Data to provide the Site and Services and to
            comply with applicable laws. Specifically, we may use your
            Personal Data in the following ways:
          </p>
          <ul>
            <li>To provide the Services to our clients and the clients of Third-Party Clients.</li>
            <li>To optimize and customize your experience when using the Site.</li>
            <li>
              To enable you to search or use third-party real estate listings
              (which may include the MLS®). This requires us to share your
              Personal Data with third parties to provide these services.
            </li>
            <li>To present offers that we believe you may be interested in.</li>
            <li>To work with you as a client, either of Kristan Cole Network or of a Third-Party Client.</li>
            <li>To contact you about the Site, our Services, or other features we may offer.</li>
            <li>
              To provide an email newsletter or other administrative or
              marketing messages via email. You may unsubscribe at any time
              by following the links in any emails sent by us.
            </li>
            <li>
              To process any payments you may make. This may require us to
              share your Personal Data with third parties to provide these
              services.
            </li>
            <li>To provide customer or technical support.</li>
            <li>
              To analyze the way in which you use the Site and the Services.
              We may use Google Analytics to collect information about use of
              the Site. Google Analytics collects information such as how
              often users visit the Site, what pages they visit when they do
              so, and what other sites they used prior to coming to this
              site. Google Analytics collects only the IP address assigned to
              you on the date you visit the Site, rather than your name or
              other identifying information. We do not combine the
              information collected through the use of Google Analytics with
              personally identifiable Personal Data. Although Google
              Analytics plants a permanent cookie on your web browser to
              identify you as a unique user the next time you visit this
              site, the cookie cannot be used by anyone but Google. Google's
              ability to use and share information collected by Google
              Analytics about your visits to this site is restricted by the
              Google Analytics Terms of Use (available at
              www.google.com/analytics/terms) and the Google Privacy Policy
              (available at policies.google.com/privacy). You can prevent
              Google Analytics from recognizing you on return visits to this
              site by disabling cookies on your browser.
            </li>
          </ul>
          <p>
            We respect your privacy and do not share your Personal Data except
            (i) as described in the Personal Data Collection section, (ii) as
            required by law or (iii) in defending or bringing a legal action
            in which you are involved.
          </p>

          <h3>Privacy Policy Security Topics</h3>
          <p>
            Kristan Cole Network is committed to protecting your security. We
            utilize various methods of protecting your Personal Data,
            including certain encryption. Nevertheless, we cannot guarantee
            the safety or security of any Personal Data. Further, transmission
            of Personal Data cannot be made completely secure. For that
            reason, you agree to transmit Personal Data to Kristan Cole
            Network and the Third-Party Clients solely at your own risk.
          </p>
          <p>
            You may encounter links to third party websites, property
            listings, or downloads when browsing the Site or using the
            Services ("Third Party Links"). We cannot control the content or
            security practices of these Third-Party Links. You agree to access
            Third-Party Links solely at your own risk.
          </p>

          <h3>Kristan Cole Network policy on international privacy</h3>
          <p>
            Kristan Cole Network is primarily operated and managed on servers
            located and operated within the United States. In order to provide
            our products and services to you, we may send and store your
            Personal Data outside of the country where you reside or are
            located, including to the United States. Accordingly, if you
            reside or are located outside of the United States, your Personal
            Data may be transferred outside of the country where you reside or
            are located, including countries that may not or do not provide
            the same level of protection for your Personal Data. We are
            committed to protecting the privacy and confidentiality of
            Personal Data when it is transferred. If you reside or are located
            within the European Economic Area and such transfers occur, we
            take appropriate steps to provide the same level of protection for
            the processing carried out in any such countries as you would have
            within the European Economic Area to the extent feasible under
            applicable law. By using and accessing our Services, Users who
            reside or are located in countries outside of the United States
            agree and consent to the transfer to and processing of Personal
            Data on servers located outside of the country where they reside,
            and that the protection of such information may be different than
            required under the laws of their residence or location.
          </p>

          <h3>You may delete or access your Personal Data</h3>
          <p>
            We will honor any user's request to access, correct, or delete
            Personal Data, to the extent required by applicable law, including
            the EU General Data Protection Regulation (GDPR). To request that
            we correct, delete, or export any Personal Data, please contact us
            at{" "}
            <a href="mailto:support@thekristancolenetwork.com" className="text-primary hover:underline">
              support@thekristancolenetwork.com
            </a>{" "}
            or{" "}
            <a href="tel:9073733575" className="text-primary hover:underline">
              907-373-3575
            </a>
            .
          </p>
          <p>
            Personal Data that we process shall not usually be kept for longer
            than is necessary for the relevant purpose. We may, however,
            retain Personal Data as required for legal compliance.
          </p>

          <h3>Your rights under the EU General Data Protection Regulation</h3>
          <p>
            If you live in the European Union, you have specific rights
            arising under the EU General Data Protection Regulation (GDPR).
            These are:
          </p>
          <ul>
            <li><strong>Right of Access:</strong> you have the right to be informed of, and request access to, the Personal Data we process about you.</li>
            <li><strong>Right to Rectification:</strong> you have the right to request that we amend or update your personal data where it is inaccurate or incomplete.</li>
            <li><strong>Right to Erasure:</strong> you have the right to have Kristan Cole Network delete your Personal Data.</li>
            <li><strong>Right to Restrict:</strong> you have the right to request that we temporarily or permanently stop processing your Personal Data.</li>
            <li><strong>Right to Object:</strong> you have the right to object to us processing your Personal Data on grounds relating to your particular situation or for direct marketing purposes.</li>
            <li><strong>Right to Data Portability:</strong> you have the right to request a copy of your Personal Data in electronic format and the right to transmit that personal data for use in another party's service.</li>
            <li><strong>Right not to be Subject to Automated Decision Making:</strong> you have the right to not be subject to a decision based solely on automated decision making, including profiling, where the decision would have a legal effect on you or produce a similarly significant effect.</li>
          </ul>
          <p>
            We respect your privacy and do not share your Personal Data except
            (i) as described in the Personal Data Collection section, (ii) as
            required by law or (iii) in defending or bringing a legal action
            in which you are involved.
          </p>

          <h3>Data Controller and How to Contact Us</h3>
          <p>
            The Personal Data we collect and process is controlled by Kristan
            Cole Network located in the United States. You may contact us at
            15333 N. Pima Rd, Suite 130, Scottsdale, AZ 85260, by email at{" "}
            <a href="mailto:support@thekristancolenetwork.com" className="text-primary hover:underline">
              support@thekristancolenetwork.com
            </a>{" "}
            or by phone at{" "}
            <a href="tel:9073733575" className="text-primary hover:underline">
              907-373-3575
            </a>
            .
          </p>
        </div>
      </Section>
    </SiteLayout>
  );
}
